import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import api from '../services/api';
import { APP_VERSION } from '../utils/version';
import { formatErrorMessage, showErrorMessage } from '../utils/errorHandler';

function ApplicationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editApplicationId = searchParams.get('edit');
  const isEditMode = !!editApplicationId;
  
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: new Date(),
    end_date: new Date(),
    reason: '',
    certificate: null,
    compensationAttachment: null,
    otherPaidLeaveDocument: null,
    requested_approver_id: ''
  });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [leaveDetails, setLeaveDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingApplication, setLoadingApplication] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [existingCertificate, setExistingCertificate] = useState(null);
  const [existingCompensationAttachment, setExistingCompensationAttachment] = useState(null);
  const [existingOtherPaidLeaveDocument, setExistingOtherPaidLeaveDocument] = useState(null);
  const [compensationPreviewUrl, setCompensationPreviewUrl] = useState(null);

  useEffect(() => {
    fetchLeaveTypes();
    fetchApprovers();
  }, []);

  useEffect(() => {
    if (isEditMode && editApplicationId) {
      fetchApplicationForEdit(editApplicationId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editApplicationId]);

  // Set selectedLeaveType when leaveTypes are loaded and formData.leave_type_id is set
  useEffect(() => {
    if (formData.leave_type_id && leaveTypes.length > 0) {
      const leaveType = leaveTypes.find(lt => lt.id === parseInt(formData.leave_type_id));
      if (leaveType && (!selectedLeaveType || selectedLeaveType.id !== leaveType.id)) {
        setSelectedLeaveType(leaveType);
      }
    }
  }, [formData.leave_type_id, leaveTypes, selectedLeaveType]);

  useEffect(() => {
    if (!formData.compensationAttachment) {
      setCompensationPreviewUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(formData.compensationAttachment);
    setCompensationPreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [formData.compensationAttachment]);

  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      // In edit mode, don't regenerate details on initial load (use existing data)
      // Only regenerate when dates are changed
      if (isEditMode && leaveDetails.length > 0) {
        const startDateStr = formData.start_date.toISOString().split('T')[0];
        const endDateStr = formData.end_date.toISOString().split('T')[0];
        const currentStartDate = leaveDetails[0].date;
        const currentEndDate = leaveDetails[leaveDetails.length - 1].date;
        
        // Only regenerate when dates are changed
        if (currentStartDate !== startDateStr || currentEndDate !== endDateStr) {
          generateLeaveDetails();
        }
      } else if (!isEditMode) {
        // Always generate details when creating new
        generateLeaveDetails();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.start_date, formData.end_date]);

  const fetchLeaveTypes = async () => {
    try {
      const response = await api.get('/leave-types');
      setLeaveTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch leave types');
    }
  };

  const fetchApprovers = async () => {
    try {
      const response = await api.get('/users/approvers');
      setApprovers(response.data || []);
    } catch (error) {
      console.error('Error fetching approvers list:', error);
      toast.error('Failed to fetch approvers list');
    }
  };

  const fetchGeneratedLeaveDetails = async (startDate, endDate) => {
    const response = await api.post('/utils/generate-leave-details', {
      start_date: startDate,
      end_date: endDate
    });
    return response.data.details || [];
  };

  const fetchApplicationForEdit = async (applicationId) => {
    try {
      setLoadingApplication(true);
      const response = await api.get(`/applications/${applicationId}`);
      const application = response.data;

      // Set application data to form
      setFormData({
        leave_type_id: application.leave_type_id.toString(),
        start_date: new Date(application.start_date),
        end_date: new Date(application.end_date),
        reason: application.reason || '',
        certificate: null,
        compensationAttachment: null,
        otherPaidLeaveDocument: null,
        requested_approver_id: application.requested_approver_id?.toString() || ''
      });

      // Set existing certificate information
      if (application.certificate_filename || application.certificate_google_drive_url) {
        setExistingCertificate({
          filename: application.certificate_filename,
          googleDriveUrl: application.certificate_google_drive_url
        });
      } else {
        setExistingCertificate(null);
      }

      if (application.compensation_attachment_filename || application.compensation_attachment_google_drive_url) {
        setExistingCompensationAttachment({
          filename: application.compensation_attachment_filename,
          googleDriveUrl: application.compensation_attachment_google_drive_url
        });
      } else {
        setExistingCompensationAttachment(null);
      }

      if (application.other_paid_leave_document_filename || application.other_paid_leave_document_google_drive_url) {
        setExistingOtherPaidLeaveDocument({
          filename: application.other_paid_leave_document_filename,
          googleDriveUrl: application.other_paid_leave_document_google_drive_url
        });
      } else {
        setExistingOtherPaidLeaveDocument(null);
      }

      // Set leave type
      const leaveType = leaveTypes.find(lt => lt.id === application.leave_type_id);
      if (leaveType) {
        setSelectedLeaveType(leaveType);
      }

      // Set detail data
      if (application.details && application.details.length > 0) {
        const formattedDetails = application.details.map(detail => ({
          date: detail.leave_date,
          period: detail.period,
          hours: parseFloat(detail.hours)
        }));
        setLeaveDetails(formattedDetails);
      } else {
        const generatedDetails = await fetchGeneratedLeaveDetails(
          application.start_date,
          application.end_date
        );
        setLeaveDetails(generatedDetails);
      }
    } catch (error) {
      console.error('Application fetch error:', error);
      toast.error('Failed to fetch application data');
      navigate('/my-applications');
    } finally {
      setLoadingApplication(false);
    }
  };

  const generateLeaveDetails = async () => {
    try {
      const details = await fetchGeneratedLeaveDetails(
        formData.start_date.toISOString().split('T')[0],
        formData.end_date.toISOString().split('T')[0]
      );
      setLeaveDetails(details);
    } catch (error) {
      console.error('Error generating details:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'leave_type_id') {
      const leaveType = leaveTypes.find(lt => lt.id === parseInt(value));
      setSelectedLeaveType(leaveType);
      setFormData(prev => ({
        ...prev,
        leave_type_id: value,
        compensationAttachment:
          leaveType?.code !== 'COMPENSATION' ? null : prev.compensationAttachment,
        otherPaidLeaveDocument:
          leaveType?.code !== 'OTHER_PAID' ? null : prev.otherPaidLeaveDocument
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      certificate: file
    }));
    // 新しいファイルが選択された場合、既存ファイルの情報をクリア（置き換えの準備）
    if (file && existingCertificate) {
      // 既存ファイルは置き換えられるため、表示から削除する準備
    }
  };

  const handleCompensationFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData(prev => ({
      ...prev,
      compensationAttachment: file || null
    }));
  };

  const handleOtherPaidLeaveDocumentFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      otherPaidLeaveDocument: file || null
    }));
  };

  const handleCompensationPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items || !selectedLeaveType || selectedLeaveType.code !== 'COMPENSATION') {
      return;
    }
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (!blob) break;
        let ext = 'png';
        if (blob.type === 'image/jpeg' || blob.type === 'image/jpg') ext = 'jpg';
        else if (blob.type === 'image/gif') ext = 'gif';
        else if (blob.type === 'image/webp') ext = 'webp';
        const mime = blob.type && blob.type.startsWith('image/')
          ? blob.type
          : 'image/png';
        const uniqueSuffix = `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
        const file = new File([blob], `${uniqueSuffix}.${ext}`, { type: mime });
        setFormData(prev => ({
          ...prev,
          compensationAttachment: file
        }));
        toast.success('Image pasted');
        e.preventDefault();
        break;
      }
    }
  };

  const clearCompensationAttachment = () => {
    setFormData(prev => ({
      ...prev,
      compensationAttachment: null
    }));
  };

  const handlePeriodChange = (detailIndex, period) => {
    const updatedDetails = [...leaveDetails];
    updatedDetails[detailIndex] = {
      ...updatedDetails[detailIndex],
      period: period,
      hours: period === 'full' ? 8.0 : 4.0
    };
    setLeaveDetails(updatedDetails);
  };

  // Check period continuity
  const validateContinuity = () => {
    if (leaveDetails.length <= 1) return { isValid: true, message: '' };

    for (let i = 0; i < leaveDetails.length - 1; i++) {
      const current = leaveDetails[i];
      const next = leaveDetails[i + 1];
      
      const currentDate = new Date(current.date);
      const nextDate = new Date(next.date);
      
      // Check if dates are consecutive
      const nextDay = new Date(currentDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      if (nextDate.getTime() === nextDay.getTime()) {
        // For consecutive dates, check period continuity
        if (current.period === 'am' && next.period === 'pm') {
          return {
            isValid: false,
            message: `There is a gap between ${formatDate(currentDate)} AM and ${formatDate(nextDate)} PM. Please specify continuous periods.`
          };
        }
        if (current.period === 'am' && next.period === 'full') {
          return {
            isValid: false,
            message: `There is a gap between ${formatDate(currentDate)} AM and ${formatDate(nextDate)} full day. Please specify continuous periods.`
          };
        }
      }
    }
    
    return { isValid: true, message: '' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Continuity check
      const continuityCheck = validateContinuity();
      if (!continuityCheck.isValid) {
        toast.error(continuityCheck.message);
        setLoading(false);
        return;
      }

      if (!formData.requested_approver_id) {
        toast.error('Please select an approver');
        setLoading(false);
        return;
      }

      const safeLeaveDetails = leaveDetails.length > 0
        ? leaveDetails
        : await fetchGeneratedLeaveDetails(
            formData.start_date.toISOString().split('T')[0],
            formData.end_date.toISOString().split('T')[0]
          );

      if (safeLeaveDetails.length === 0) {
        toast.error('Failed to prepare leave details');
        setLoading(false);
        return;
      }

      if (selectedLeaveType?.requires_certificate) {
        const hasExisting =
          isEditMode &&
          existingCertificate &&
          (existingCertificate.filename || existingCertificate.googleDriveUrl);
        if (!formData.certificate && !hasExisting) {
          toast.error('Medical certificate is required for this leave type.');
          setLoading(false);
          return;
        }
      }

      if (selectedLeaveType?.code === 'COMPENSATION') {
        const hasExistingComp =
          isEditMode &&
          existingCompensationAttachment &&
          (existingCompensationAttachment.filename ||
            existingCompensationAttachment.googleDriveUrl);
        if (!formData.compensationAttachment && !hasExistingComp) {
          toast.error(
            'Manager approval attachment (screenshot or image file) is required for compensation leave.'
          );
          setLoading(false);
          return;
        }
      }

      if (selectedLeaveType?.code === 'OTHER_PAID') {
        const hasExistingOtherPaid =
          isEditMode &&
          existingOtherPaidLeaveDocument &&
          (existingOtherPaidLeaveDocument.filename ||
            existingOtherPaidLeaveDocument.googleDriveUrl);
        if (!formData.otherPaidLeaveDocument && !hasExistingOtherPaid) {
          toast.error('A supporting document is required for Other Paid Leave.');
          setLoading(false);
          return;
        }
      }

      const applicationData = {
        leave_type_id: parseInt(formData.leave_type_id),
        start_date: formData.start_date.toISOString().split('T')[0],
        end_date: formData.end_date.toISOString().split('T')[0],
        reason: formData.reason,
        requested_approver_id: parseInt(formData.requested_approver_id),
        leave_details: safeLeaveDetails.map(detail => ({
          date: detail.date,
          period: detail.period,
          hours: detail.hours
        }))
      };

      let certificate_filename;
      let certificate_google_drive_url;
      if (formData.certificate) {
        const startYmd = formData.start_date.toISOString().split('T')[0];
        const endYmd = formData.end_date.toISOString().split('T')[0];
        const fileFormData = new FormData();
        fileFormData.append('certificate', formData.certificate);
        fileFormData.append('start_date', startYmd);
        fileFormData.append('end_date', endYmd);
        const uploadRes = await api.post('/upload/certificate', fileFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        certificate_filename = uploadRes.data.filename;
        if (uploadRes.data.google_drive_url) {
          certificate_google_drive_url = uploadRes.data.google_drive_url;
        }
      }

      let compensation_attachment_filename;
      let compensation_attachment_google_drive_url;
      if (selectedLeaveType?.code === 'COMPENSATION' && formData.compensationAttachment) {
        const startYmdComp = formData.start_date.toISOString().split('T')[0];
        const endYmdComp = formData.end_date.toISOString().split('T')[0];
        const compFormData = new FormData();
        compFormData.append('compensation_attachment', formData.compensationAttachment);
        compFormData.append('start_date', startYmdComp);
        compFormData.append('end_date', endYmdComp);
        const compUploadRes = await api.post('/upload/compensation-attachment', compFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        compensation_attachment_filename = compUploadRes.data.filename;
        if (compUploadRes.data.google_drive_url) {
          compensation_attachment_google_drive_url = compUploadRes.data.google_drive_url;
        }
      }

      let other_paid_leave_document_filename;
      let other_paid_leave_document_google_drive_url;
      if (selectedLeaveType?.code === 'OTHER_PAID' && formData.otherPaidLeaveDocument) {
        const startYmdOther = formData.start_date.toISOString().split('T')[0];
        const endYmdOther = formData.end_date.toISOString().split('T')[0];
        const otherPaidFormData = new FormData();
        otherPaidFormData.append('other_paid_leave_document', formData.otherPaidLeaveDocument);
        otherPaidFormData.append('start_date', startYmdOther);
        otherPaidFormData.append('end_date', endYmdOther);
        const otherPaidUploadRes = await api.post('/upload/other-paid-leave-document', otherPaidFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        other_paid_leave_document_filename = otherPaidUploadRes.data.filename;
        if (otherPaidUploadRes.data.google_drive_url) {
          other_paid_leave_document_google_drive_url = otherPaidUploadRes.data.google_drive_url;
        }
      }

      if (isEditMode && editApplicationId) {
        const putBody = { ...applicationData };
        if (certificate_filename) {
          putBody.certificate_filename = certificate_filename;
        }
        if (certificate_google_drive_url) {
          putBody.certificate_google_drive_url = certificate_google_drive_url;
        }
        if (compensation_attachment_filename) {
          putBody.compensation_attachment_filename = compensation_attachment_filename;
        }
        if (compensation_attachment_google_drive_url) {
          putBody.compensation_attachment_google_drive_url = compensation_attachment_google_drive_url;
        }
        if (other_paid_leave_document_filename) {
          putBody.other_paid_leave_document_filename = other_paid_leave_document_filename;
        }
        if (other_paid_leave_document_google_drive_url) {
          putBody.other_paid_leave_document_google_drive_url = other_paid_leave_document_google_drive_url;
        }
        await api.put(`/applications/${editApplicationId}`, putBody);

        toast.success('Application updated successfully');
      } else {
        const postBody = { ...applicationData };
        if (certificate_filename) {
          postBody.certificate_filename = certificate_filename;
        }
        if (certificate_google_drive_url) {
          postBody.certificate_google_drive_url = certificate_google_drive_url;
        }
        if (compensation_attachment_filename) {
          postBody.compensation_attachment_filename = compensation_attachment_filename;
        }
        if (compensation_attachment_google_drive_url) {
          postBody.compensation_attachment_google_drive_url = compensation_attachment_google_drive_url;
        }
        if (other_paid_leave_document_filename) {
          postBody.other_paid_leave_document_filename = other_paid_leave_document_filename;
        }
        if (other_paid_leave_document_google_drive_url) {
          postBody.other_paid_leave_document_google_drive_url = other_paid_leave_document_google_drive_url;
        }
        await api.post('/applications', postBody);

        toast.success('Application submitted successfully');
      }

      navigate('/my-applications');

    } catch (error) {
      console.error('Application submission error:', error);
      const errData = error.response?.data;
      if (errData?.error === 'Insufficient annual leave' && errData?.message) {
        window.alert(errData.message);
      } else {
        const message = formatErrorMessage(error);
        showErrorMessage(toast.error, message);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const getTotalDays = () => {
    if (!leaveDetails || leaveDetails.length === 0) {
      return 0;
    }
    return leaveDetails.reduce((total, detail) => {
      if (!detail || typeof detail.hours !== 'number') {
        return total;
      }
      return total + detail.hours / 8;
    }, 0);
  };

  if (loadingApplication) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>{isEditMode ? 'Edit Application' : 'Leave Application'}</h1>
          {isEditMode && editApplicationId && (
            <div style={{ marginTop: '0.5rem' }}>
              <span className="text-muted">Application ID: #{editApplicationId}</span>
            </div>
          )}
          <small className="text-muted">{APP_VERSION}</small>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="card">
        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Leave Type *</label>
            <select
              name="leave_type_id"
              className="form-control notranslate"
              translate="no"
              value={formData.leave_type_id}
              onChange={handleChange}
              required
            >
              <option value="">Please select</option>
              {leaveTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Approver *</label>
            <select
              name="requested_approver_id"
              className="form-control notranslate"
              translate="no"
              value={formData.requested_approver_id}
              onChange={handleChange}
              required
            >
              <option value="">Please select approver</option>
              {approvers.map(approver => (
                <option key={approver.id} value={approver.id}>
                  {approver.full_name} ({approver.department || 'No Department'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Reason for Leave - Optional */}
        <div className="form-group">
          <label className="form-label">Reason for Leave</label>
          <textarea
            name="reason"
            className="form-control"
            rows="3"
            value={formData.reason}
            onChange={handleChange}
            placeholder="Please enter the reason for your leave (optional)"
          />
        </div>

        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Start Date *</label>
            <DatePicker
              selected={formData.start_date}
              onChange={date => handleDateChange(date, 'start_date')}
              className="form-control"
              dateFormat="yyyy/MM/dd"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">End Date *</label>
            <DatePicker
              selected={formData.end_date}
              onChange={date => handleDateChange(date, 'end_date')}
              className="form-control"
              dateFormat="yyyy/MM/dd"
              minDate={formData.start_date}
              required
            />
          </div>
        </div>

        {/* Medical Certificate Upload */}
        {Boolean(selectedLeaveType?.requires_certificate) && (
          <div className="form-group">
            <label className="form-label">Medical Certificate *</label>
            
            {/* Display existing file (in edit mode when existing file exists) */}
            {isEditMode && existingCertificate && !formData.certificate && (
              <div className="existing-certificate" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>
                    📎 Existing file:
                  </span>
                  {existingCertificate.googleDriveUrl ? (
                    <a
                      href={existingCertificate.googleDriveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Download (Google Drive)
                    </a>
                  ) : existingCertificate.filename ? (
                    <a
                      href={`/uploads/${existingCertificate.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Download
                    </a>
                  ) : null}
                </div>
                <small className="text-muted" style={{ display: 'block', marginTop: '0.25rem' }}>
                  Selecting a new file will replace the existing file
                </small>
              </div>
            )}
            
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            <small className="form-text text-muted">
              Required before submit. You can use PDF, JPG, or PNG (max 5MB).
            </small>
          </div>
        )}

        {selectedLeaveType?.code === 'COMPENSATION' && (
          <div className="form-group">
            <label className="form-label">Manager approval attachment (screenshot) *</label>
            {isEditMode && existingCompensationAttachment && !formData.compensationAttachment && (
              <div
                className="existing-certificate"
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>Existing attachment:</span>
                  {existingCompensationAttachment.googleDriveUrl ? (
                    <a
                      href={existingCompensationAttachment.googleDriveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Open (Google Drive)
                    </a>
                  ) : existingCompensationAttachment.filename ? (
                    <a
                      href={`/uploads/${existingCompensationAttachment.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Open
                    </a>
                  ) : null}
                </div>
                <small className="text-muted" style={{ display: 'block', marginTop: '0.25rem' }}>
                  Choosing a new image or paste will replace this file.
                </small>
              </div>
            )}
            <input
              type="file"
              className="form-control mb-2"
              accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleCompensationFileChange}
            />
            <div
              className="compensation-paste-zone"
              tabIndex={0}
              onPaste={handleCompensationPaste}
              role="textbox"
              aria-label="Paste screenshot from clipboard"
            >
              Required before submit. Focus here and paste an image from the clipboard (Ctrl+V / ⌘V), or
              use the file picker above. PNG, JPG, GIF, WebP — max 5MB.
            </div>
            {compensationPreviewUrl && (
              <div className="compensation-preview mt-2">
                <img src={compensationPreviewUrl} alt="Screenshot preview" style={{ maxWidth: '100%', maxHeight: '240px', borderRadius: '4px', border: '1px solid #dee2e6' }} />
                <div className="mt-2">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearCompensationAttachment}>
                    Clear image
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {selectedLeaveType?.code === 'OTHER_PAID' && (
          <div className="form-group">
            <label className="form-label">Supporting document *</label>
            {isEditMode && existingOtherPaidLeaveDocument && !formData.otherPaidLeaveDocument && (
              <div
                className="existing-certificate"
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>Existing file:</span>
                  {existingOtherPaidLeaveDocument.googleDriveUrl ? (
                    <a
                      href={existingOtherPaidLeaveDocument.googleDriveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Open (Google Drive)
                    </a>
                  ) : existingOtherPaidLeaveDocument.filename ? (
                    <a
                      href={`/uploads/${existingOtherPaidLeaveDocument.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Download
                    </a>
                  ) : null}
                </div>
                <small className="text-muted" style={{ display: 'block', marginTop: '0.25rem' }}>
                  Selecting a new file will replace the existing file
                </small>
              </div>
            )}
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleOtherPaidLeaveDocumentFileChange}
            />
            <small className="form-text text-muted d-block mt-2">
              Required before submit. PDF, JPG, or PNG (max 5MB).
            </small>
            <ul className="form-text text-muted mt-2 mb-0" style={{ paddingLeft: '1.25rem' }}>
              <li>
                <strong>Marriage:</strong> Please upload your marriage certificate or wedding invitation.
              </li>
              <li>
                <strong>Bereavement:</strong> Please upload a death certificate or a proof of funeral arrangements.
              </li>
              <li>
                <strong>Other:</strong> Please upload relevant supporting documents for your request.
              </li>
            </ul>
          </div>
        )}

        {/* Detailed Settings */}
        {leaveDetails && leaveDetails.length > 0 && (
          <div className="leave-details-section">
            <h3>Detailed Settings</h3>
            <p className="text-muted">
              Configure leave duration for each date. Total: {getTotalDays()} day(s)
            </p>
            
            {/* Continuity Check Warning */}
            {(() => {
              const continuityCheck = validateContinuity();
              return !continuityCheck.isValid && (
                <div className="alert alert-warning">
                  <strong>⚠️ Period Continuity Error:</strong><br />
                  {continuityCheck.message}
                </div>
              );
            })()}
            
            <div className="details-grid">
              {leaveDetails.map((detail, index) => (
                <div key={index} className="detail-item card">
                  <div className="detail-date">
                    {formatDate(new Date(detail.date))}
                  </div>
                  <div className="period-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name={`period-${index}`}
                        value="full"
                        checked={detail.period === 'full'}
                        onChange={() => handlePeriodChange(index, 'full')}
                      />
                      <span>Full Day (8 hours)</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name={`period-${index}`}
                        value="am"
                        checked={detail.period === 'am'}
                        onChange={() => handlePeriodChange(index, 'am')}
                      />
                      <span>AM (4 hours)</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name={`period-${index}`}
                        value="pm"
                        checked={detail.period === 'pm'}
                        onChange={() => handlePeriodChange(index, 'pm')}
                      />
                      <span>PM (4 hours)</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="continuity-help">
              <h4>📋 Continuous Period Rules</h4>
              <ul>
                <li><strong>✅ Valid:</strong> 12th PM → 13th AM (continuous)</li>
                <li><strong>✅ Valid:</strong> 12th PM → 13th Full Day (continuous)</li>
                <li><strong>✅ Valid:</strong> 12th Full Day → 13th AM (continuous)</li>
                <li><strong>❌ Invalid:</strong> 12th AM → 13th PM (12th PM is missing)</li>
                <li><strong>❌ Invalid:</strong> 12th AM → 13th Full Day (12th PM is missing)</li>
              </ul>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => navigate('/my-applications')}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading || !formData.leave_type_id || !formData.requested_approver_id}
          >
            {loading 
              ? (isEditMode ? 'Updating...' : 'Submitting...') 
              : (isEditMode ? 'Update Application' : 'Submit Application')
            }
          </button>
        </div>
      </form>

      <style>{`
        .compensation-paste-zone {
          padding: 0.85rem 1rem;
          border: 1px dashed #adb5bd;
          border-radius: 8px;
          background: #fafbfc;
          color: #6c757d;
          font-size: 0.9rem;
          line-height: 1.45;
          outline: none;
        }

        .compensation-paste-zone:focus-visible {
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.2);
        }

        .leave-details-section {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid #dee2e6;
        }
        
        .continuity-help {
          margin-top: 1.5rem;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #007bff;
        }
        
        .continuity-help h4 {
          margin-bottom: 0.75rem;
          color: #495057;
        }
        
        .continuity-help ul {
          margin-bottom: 0;
          padding-left: 1.25rem;
        }
        
        .continuity-help li {
          margin-bottom: 0.25rem;
        }
        
        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .detail-item {
          padding: 1rem;
          border: 1px solid #dee2e6;
          border-radius: 8px;
        }
        
        .detail-date {
          font-weight: bold;
          margin-bottom: 0.5rem;
          color: #495057;
        }
        
        .period-options {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .radio-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          transition: background-color 0.2s;
        }
        
        .radio-option:hover {
          background-color: #f8f9fa;
        }
        
        .radio-option input[type="radio"] {
          margin: 0;
        }
        
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid #dee2e6;
        }
        
        @media (max-width: 768px) {
          .details-grid {
            grid-template-columns: 1fr;
          }
          
          .form-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

export default ApplicationForm;